// ─── SMS COMPOSE HELPERS ──────────────────────────────────────────────────────
// Building an sms: deep link so a prefilled text opens in the user's own
// Messages app — nothing here actually sends anything.

import { fmtTime, fmtDayDate, fmtTimeToEvent, fmtClockTime } from './datetime';
import { stripFohMeta } from '../api/assignments';

// Fills a coverage-request template's placeholder tokens from an event.
// Supported tokens: {title} {description} {date} {time} {time_to_event}.
// GCal-style `start` — { dateTime } for timed events, { date } for all-day.
export function renderCoverageMessage(template, { eventTitle, description = '', start, now = new Date() } = {}) {
  const allDay = !start?.dateTime;
  const map = {
    title:         (eventTitle && eventTitle.trim()) || 'this',
    description:   stripFohMeta(description),
    date:          start ? fmtDayDate((start.dateTime || start.date || '').split('T')[0]) : '',
    time:          allDay ? 'all day' : fmtTime(start.dateTime),
    time_to_event: fmtTimeToEvent(start, now),
  };
  return (template || '').replace(/\{(title|description|date|time|time_to_event)\}/g, (_, k) => map[k] ?? '');
}

// Fills a caregiver-decline template's placeholder tokens from a sessions
// doc — kept separate from renderCoverageMessage above (different input
// shape: a session doc with plain "HH:MM" times, not a GCal event) so
// changes here can't regress the two already-shipped coverage templates.
// Supported tokens: {parents} {date} {time_range}.
export function renderCaregiverDeclineMessage(template, { session, carePartners = [] } = {}) {
  const names = carePartners.map(cp => cp.name?.split(' ')[0] || cp.name).filter(Boolean);
  const parents = names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names.slice(-1)}` : (names[0] || 'there');
  const map = {
    parents,
    date: session?.date ? fmtDayDate(session.date) : '',
    time_range: session?.startTime ? `${fmtClockTime(session.startTime)}–${fmtClockTime(session.endTime)}` : '',
  };
  return (template || '').replace(/\{(parents|date|time_range)\}/g, (_, k) => map[k] ?? '');
}

// iPadOS reports navigator.platform as "MacIntel" — the touch-points check
// distinguishes it from an actual Mac.
function isIOS() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const platform = (typeof navigator !== 'undefined' && navigator.platform) || '';
  const maxTouchPoints = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0;
  return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

// Phone fields are free-text everywhere in this app (no format enforcement),
// so normalize before it's usable in an sms: URI. iOS wants "&body=", every
// other platform wants the standard "?body=".
export function smsHref(phone, body) {
  const digits = (phone || '').replace(/[^\d+]/g, '');
  const sep = isIOS() ? '&' : '?';
  return `sms:${digits}${sep}body=${encodeURIComponent(body || '')}`;
}
